import { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import {
	Button,
	Card,
	Switch,
	DatePicker,
	Descriptions,
	Dropdown,
	Input,
	Modal,
	Select,
	Space,
	Spin,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd';
import {
	DownOutlined,
	PlusCircleOutlined,
	SyncOutlined,
	EditOutlined,
	CloseOutlined,
} from '@ant-design/icons';
import getSymbolFromCurrency from 'currency-symbol-map';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import get from 'lodash/get';
import { useHistory } from 'react-router-dom';
import { BONUS_STATUS, USER_ROLES } from '../constants';
import {
	formatDate,
	mapBonusStatus,
	mapReferralStatus,
	ml,
	parse,
} from '../services/utils';
import {
	updateBonusStatus,
	revalidateBonus,
} from 'src/_shared/referral/referralActions';
import { generateBonusLogs } from 'src/_shared/api/components/logging/LoggingUtils.jsx';
import { currencies } from 'src/settings-dashboard/referral-tiers/commonCurrency.js';

dayjs.extend(utc);

const { Title, Link, Text } = Typography;
const { TextArea } = Input;

const BonusDetailsModal = (props) => {
	const {
		onClose,
		currentUser,
		allMultiLingualData,
		updateBonusStatus,
		fetchEligibilityResults,
		fetchingEligibility,
		revalidatingBonuses,
		revalidateBonus,
	} = props;
	const [bonus, setBonus] = useState(props?.bonus);
	const [isRevalidating, setIsRevalidating] = useState(
		Object.keys(revalidatingBonuses)?.length > 0
	);
	const [editMode, setEditMode] = useState(false);
	const [reasonForChange, setReasonForChange] = useState('');
	const [validationError, setValidationError] = useState('');

	// Form fields
	const [notes, setNotes] = useState(bonus.notes || '');
	const [bonusStatus, setBonusStatus] = useState(bonus.bonusStatus || null);
	const [amountDue, setAmountDue] = useState(
		(bonus.amountDue && Number(bonus.amountDue)) || 0
	);
	const [startDate, setStartDate] = useState(
		(bonus.startDate && dayjs.utc(bonus.startDate)) || null
	);
	const [earnedDate, setEarnedDate] = useState(
		(bonus.earnedDate && dayjs.utc(bonus.earnedDate)) || null
	);
	const [paidDate, setPaidDate] = useState(
		(bonus.paidDate && dayjs.utc(bonus.paidDate)) || null
	);
	const [ignoreEligibilityChecks, setIgnoreEligibilityChecks] = useState(
		bonus?.ignoreEligibilityChecks || false
	);
	const [currency, setCurrency] = useState(
		bonus?.currency || get(bonus, 'user.userGroup.currency', 'USD')
	);
	const [translatedJobTitle, setTranslatedJobTitle] = useState('');

	const history = useHistory();
	const recipientType = bonus.recipientType || null;

	useEffect(() => {
		if (
			isRevalidating &&
			(bonus?.referral?.eligibilityResults || bonus?.user?.title)
		) {
			setIsRevalidating(false);
		}
	}, [bonus?.referral?.eligibilityResults]);

	useEffect(() => {
		if (bonus.referralId && !bonus.referral?.eligibilityResults) {
			fetchEligibilityResults(bonus.referralId, bonus.id);
		}
	}, [
		bonus.referralId,
		bonus.id,
		bonus.referral?.eligibilityResults,
		fetchEligibilityResults,
	]);

	useEffect(() => {
		setTranslatedJobTitle(get(bonus, 'job.title'));
	}, [get(bonus, 'job.title'), currentUser]);

	useEffect(() => {
		if (props?.bonus) {
			setBonus(props.bonus);
			setBonusStatus(props.bonus?.bonusStatus);
			setIgnoreEligibilityChecks(props.bonus?.ignoreEligibilityChecks || false);
			setNotes(props.bonus?.notes || '');
			setAmountDue(
				(props.bonus?.amountDue && Number(props.bonus.amountDue)) || 0
			);
			setStartDate(
				(props.bonus?.startDate && dayjs.utc(props.bonus.startDate)) || null
			);
			setEarnedDate(
				(props.bonus?.earnedDate && dayjs.utc(props.bonus.earnedDate)) || null
			);
			setPaidDate(
				(props.bonus?.paidDate && dayjs.utc(props.bonus.paidDate)) || null
			);
			setCurrency(
				props.bonus?.currency ||
					get(props.bonus, 'user.userGroup.currency', 'USD')
			);
			setTranslatedJobTitle(props?.bonus?.job?.title || '');
		}
	}, [props?.bonus]);

	// Computed values
	const referringUserData = useMemo(
		() => bonus.referral?.user || {},
		[bonus.referral]
	);
	const eligibilityResults = useMemo(() => {
		if (!bonus.referral?.eligibilityResults) return [];
		try {
			return JSON.parse(bonus.referral.eligibilityResults);
		} catch (e) {
			console.error('Error parsing eligibility results:', e);
			return [];
		}
	}, [bonus.referral?.eligibilityResults]);

	const languageCode = get(currentUser, 'languageCode', 'US');
	const dateFormat = get(currentUser, 'dateFormat');
	const displayAs = get(currentUser, 'displayAs');

	const managerBonuses = useMemo(() => {
		return currentUser.company?.disableManagerPermissionsByType
			? JSON.parse(currentUser.company.disableManagerPermissionsByType)
					.managerBonuses
			: 'hidden';
	}, [currentUser.company]);

	const isManagerPermissionDisabled = managerBonuses !== 'edit';
	const canEdit = !(
		isManagerPermissionDisabled && displayAs === USER_ROLES.MANAGER
	);

	const symbol = getSymbolFromCurrency(currency);

	const referrer = useMemo(
		() => ({
			firstName: get(bonus, 'user.firstName'),
			lastName: get(bonus, 'user.lastName'),
			URL: `/employees/${bonus.userId}`,
		}),
		[bonus]
	);

	const referredCandidate = useMemo(
		() => ({
			firstName: get(bonus, 'contact.firstName'),
			lastName: get(bonus, 'contact.lastName'),
			URL: `/referrals/${bonus.referralId}`,
		}),
		[bonus]
	);

	const bonusRecipient = useMemo(() => {
		return bonus.recipientType === 'employee'
			? {
					firstName: get(bonus, 'user.firstName'),
					lastName: get(bonus, 'user.lastName'),
					URL: `/employees/${bonus.userId}`,
				}
			: {
					firstName: get(bonus, 'contact.firstName'),
					lastName: get(bonus, 'contact.lastName'),
					URL: `/referrals/${bonus.referralId}`,
				};
	}, [bonus]);

	// Helper functions
	const navigateTo = useCallback(
		(URL) => {
			history.push(URL);
		},
		[history]
	);

	const generateLogs = useCallback(() => {
		const updatedFields = {
			amountDue,
			startDate: startDate && startDate.toISOString(),
			earnedDate: earnedDate && earnedDate.toISOString(),
			paidDate: paidDate && paidDate.toISOString(),
			bonusStatus,
			currency,
			notes: notes ? notes : null,
			ignoreEligibilityChecks,
		};

		return generateBonusLogs(
			bonus,
			updatedFields,
			currentUser.emailAddress,
			reasonForChange
		);
	}, [
		amountDue,
		startDate,
		earnedDate,
		paidDate,
		bonusStatus,
		currency,
		notes,
		ignoreEligibilityChecks,
		bonus,
		currentUser.emailAddress,
		reasonForChange,
	]);

	const submitChanges = useCallback(() => {
		const logEntries = generateLogs();
		if (logEntries.length === 0) {
			setEditMode(false);
			setReasonForChange('');
			setValidationError('');
			return;
		}

		if (!reasonForChange.trim() && bonus.referralId) {
			setValidationError(
				ml('Reason for change required', currentUser, allMultiLingualData)
			);
			return; // Don't proceed with submission
		}

		// Clear validation error if validation passes
		setValidationError('');

		const updatedBonus = {
			id: bonus.id,
			earnedDate: earnedDate && earnedDate.toISOString(),
			startDate: startDate && startDate.toISOString(),
			paidDate: paidDate && paidDate.toISOString(),
			recipientType,
			bonusStatus,
			amountDue,
			currency,
			notes,
			ignoreEligibilityChecks,
		};

		updateBonusStatus(
			updatedBonus,
			bonus.referralId,
			logEntries,
			eligibilityResults
		)
			.then(() => {
				setEditMode(false);
				setReasonForChange('');
				setValidationError('');
				message.success('Bonus successfully updated.');
				onClose();
			})
			.catch((error) => {
				console.error('Error updating bonus:', error);
				message.error('Failed to update bonus. Please try again.');
			});
	}, [
		bonus.id,
		bonus.referralId,
		earnedDate,
		startDate,
		paidDate,
		recipientType,
		bonusStatus,
		amountDue,
		notes,
		ignoreEligibilityChecks,
		reasonForChange,
		currentUser,
		allMultiLingualData,
		updateBonusStatus,
		setBonus,
		onClose,
		generateLogs,
	]);

	const handleReasonForChange = useCallback(
		(e) => {
			const newValue = e.target.value;
			setReasonForChange(newValue);

			// Clear validation error when user starts typing
			if (newValue.trim() && validationError) {
				setValidationError('');
			}
		},
		[validationError]
	);

	const handleEditToggle = () => {
		setEditMode(!editMode);

		if (editMode) {
			setNotes(bonus.notes || '');
			setBonusStatus(bonus.bonusStatus || null);
			setAmountDue((bonus.amountDue && Number(bonus.amountDue)) || 0);
			setStartDate((bonus.startDate && dayjs.utc(bonus.startDate)) || null);
			setEarnedDate((bonus.earnedDate && dayjs.utc(bonus.earnedDate)) || null);
			setPaidDate((bonus.paidDate && dayjs.utc(bonus.paidDate)) || null);
			setCurrency(
				bonus?.currency || get(bonus, 'user.userGroup.currency', 'USD')
			);
			setIgnoreEligibilityChecks(bonus.ignoreEligibilityChecks || false);
			setReasonForChange('');
			setValidationError('');
		}
	};

	const renderLogs = useCallback(() => {
		if (
			!bonus.referralId ||
			!eligibilityResults ||
			eligibilityResults.length === 0
		) {
			return null;
		}

		if (isRevalidating) {
			return (
				<Card size="small" style={{ marginTop: 16 }}>
					<Title level={5}>
						{ml('Logs', currentUser, allMultiLingualData)}
					</Title>
					<div style={{ textAlign: 'center', padding: '20px' }}>
						<Spin size="small" />
						<div style={{ marginTop: '10px' }}>
							Revalidating bonus... This may take a moment.
						</div>
					</div>
				</Card>
			);
		}

		if (!eligibilityResults || eligibilityResults.length === 0) {
			const content = fetchingEligibility ? (
				<div style={{ textAlign: 'center', padding: '10px' }}>
					<Spin size="small" />
					Loading eligibility records...
				</div>
			) : (
				<div style={{ textAlign: 'center', padding: '10px' }}>
					No eligibility records found.
				</div>
			);

			return (
				<Card size="small" style={{ marginTop: 16 }}>
					<Title level={5}>
						{ml('Logs', currentUser, allMultiLingualData)}
					</Title>
					{content}
				</Card>
			);
		}

		const parsedEligibilityResults = parse(eligibilityResults);

		return (
			<Card size="small" style={{ marginTop: 16 }}>
				<Title level={5}>{ml('Logs', currentUser, allMultiLingualData)}</Title>
				<div style={{ maxHeight: '200px', overflow: 'auto' }}>
					{parsedEligibilityResults.map((entry, index) => (
						<div
							key={index}
							style={{
								marginBottom: 8,
								padding: 8,
								backgroundColor: '#fafafa',
								borderRadius: 4,
							}}
						>
							<Text strong>
								{formatDate(get(entry, 'date')) +
									(get(entry, 'results')
										? ' Eligibility Check - '
										: ' Bonus Record Change - ')}
							</Text>
							<div>{get(entry, 'results') ?? get(entry, 'changeLog')}</div>
						</div>
					))}
				</div>
			</Card>
		);
	}, [
		bonus.referralId,
		eligibilityResults,
		currentUser,
		allMultiLingualData,
		fetchingEligibility,
		isRevalidating,
	]);

	const getBonusTagColor = (bonusStatus) => {
		const bonusStatusColors = {
			pending: 'var(--tan-hide)',
			needsReview: 'var(--mellow-yellow)',
			earned: 'var(--fern)',
			ineligible: 'var(--sunset-orange)',
			paid: 'var(--forest-green)',
		};
		return bonusStatusColors[bonusStatus] || 'default';
	};

	const preStartUserGroupName = currentUser?.company?.userGroups.find(
		(group) => group.id === currentUser?.company.preStartUserGroupId
	)?.name;
	const preStartEnabled =
		currentUser?.company?.preStartEnabled &&
		preStartUserGroupName &&
		bonus?.origin === 'pre_start';

	return (
		<Modal
			title={
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					{ml('Bonus Details', currentUser, allMultiLingualData)}
					{canEdit && (
						<Button
							type="link"
							icon={editMode ? <CloseOutlined /> : <EditOutlined />}
							onClick={handleEditToggle}
							disabled={isRevalidating}
						>
							{editMode
								? ml('Cancel', currentUser, allMultiLingualData)
								: ml('Edit Bonus', currentUser, allMultiLingualData)}
						</Button>
					)}
				</div>
			}
			visible={true}
			width={800}
			footer={
				<div style={{ textAlign: 'right' }}>
					{editMode ? (
						<Space>
							<Button
								onClick={handleEditToggle}
								disabled={isRevalidating}
								style={{ minWidth: '80px' }}
							>
								{ml('Cancel', currentUser, allMultiLingualData)}
							</Button>
							<Button
								type="primary"
								onClick={submitChanges}
								disabled={isRevalidating}
								style={{ minWidth: '80px' }}
							>
								{ml('Save', currentUser, allMultiLingualData)}
							</Button>
						</Space>
					) : (
						<Button
							type="primary"
							onClick={() => onClose()}
							disabled={isRevalidating}
							style={{ minWidth: '80px' }}
						>
							{ml('Close', currentUser, allMultiLingualData)}
						</Button>
					)}
				</div>
			}
			closable={false}
			maskClosable={false}
		>
			<Descriptions
				bordered
				column={1}
				size="small"
				labelStyle={{
					backgroundColor: '#fafafa',
					fontWeight: 'bold',
					color: '#000000',
					width: '240px',
					padding: '8px 12px',
				}}
				contentStyle={{
					padding: '8px 12px',
				}}
			>
				<Descriptions.Item
					label={ml('Recipient Name', currentUser, allMultiLingualData)}
				>
					<Button
						type="link"
						style={{ padding: 0, color: '#1890ff', textDecoration: 'none' }}
						onClick={() => navigateTo(bonusRecipient.URL)}
					>
						{bonusRecipient.firstName} {bonusRecipient.lastName}
					</Button>
				</Descriptions.Item>

				<Descriptions.Item
					label={ml('Recipient Type', currentUser, allMultiLingualData)}
				>
					{bonus.recipientType &&
						bonus.recipientType.charAt(0).toUpperCase() +
							bonus.recipientType.slice(1)}
				</Descriptions.Item>

				<Descriptions.Item label={ml('Job', currentUser, allMultiLingualData)}>
					<Button
						type="link"
						style={{ padding: 0, color: '#1890ff', textDecoration: 'none' }}
						onClick={() => navigateTo(`/jobs/${get(bonus, 'job.id')}`)}
					>
						{translatedJobTitle}
					</Button>
				</Descriptions.Item>

				{recipientType === 'candidate' ? (
					<Descriptions.Item
						label={`${ml(
							`${mapReferralStatus('referred', currentUser.company)} By`,
							currentUser,
							allMultiLingualData
						)}`}
					>
						<Button
							type="link"
							style={{ padding: 0, color: '#1890ff', textDecoration: 'none' }}
							onClick={() => navigateTo(referrer.URL)}
						>
							{referrer.firstName} {referrer.lastName}
						</Button>
					</Descriptions.Item>
				) : (
					<Descriptions.Item
						label={ml('Referred Candidate', currentUser, allMultiLingualData)}
					>
						<Button
							type="link"
							style={{ padding: 0, color: '#1890ff', textDecoration: 'none' }}
							onClick={() => navigateTo(referredCandidate.URL)}
						>
							{referredCandidate?.firstName} {referredCandidate?.lastName}
						</Button>
					</Descriptions.Item>
				)}

				<Descriptions.Item
					label={ml('Bonus Amount', currentUser, allMultiLingualData)}
				>
					{editMode ? (
						<Input
							value={amountDue}
							type="number"
							onChange={(e) => setAmountDue(e.target.value)}
							style={{ width: 150 }}
						/>
					) : (
						<Text strong style={{ color: 'green' }}>
							{symbol}
							{bonus.amountDue
								.toString()
								.replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '0'}
							{preStartEnabled && (
								<Tag color="green" style={{ marginLeft: 6 }}>
									{preStartUserGroupName}
								</Tag>
							)}
						</Text>
					)}
				</Descriptions.Item>

				<Descriptions.Item
					label={ml('Currency', currentUser, allMultiLingualData)}
				>
					{editMode ? (
						<Select
							showSearch
							value={currency}
							style={{ width: 150 }}
							optionFilterProp="children"
							onChange={(value) => setCurrency(value)}
						>
							{Object.keys(currencies).map((key) => (
								<Select.Option key={key} value={key}>
									{getSymbolFromCurrency(key)} {key}
								</Select.Option>
							))}
						</Select>
					) : (
						<>
							{symbol} {currency}
						</>
					)}
				</Descriptions.Item>

				<Descriptions.Item
					label={ml(
						'Referring Employee Title',
						currentUser,
						allMultiLingualData
					)}
				>
					{referringUserData?.title || ''}
				</Descriptions.Item>

				{get(bonus, 'user.accountClaim.companyNumber') && (
					<Descriptions.Item label="Referring Employee Company No.">
						{bonus?.user?.accountClaim?.companyNumber}
					</Descriptions.Item>
				)}

				<Descriptions.Item
					label={ml(
						'Referring Employee Status',
						currentUser,
						allMultiLingualData
					)}
				>
					{referringUserData?.active ? 'Active' : 'Inactive'}
				</Descriptions.Item>

				{get(bonus, 'user.accountClaim.employeeId') && (
					<Descriptions.Item label="Referring Employee Number">
						{get(bonus, 'user.accountClaim.employeeId')}
					</Descriptions.Item>
				)}

				{get(bonus, 'contact.accountClaim.employeeId') && (
					<Descriptions.Item label="Referred Employee Number">
						{get(bonus, 'contact.accountClaim.employeeId')}
					</Descriptions.Item>
				)}

				<Descriptions.Item
					label={`${ml(
						`${mapReferralStatus('hired', currentUser.company)} Date`,
						currentUser,
						allMultiLingualData
					)}`}
				>
					{formatDate(bonus.hireDate, languageCode, dateFormat)}
				</Descriptions.Item>

				<Descriptions.Item
					label={ml('Start Date', currentUser, allMultiLingualData)}
				>
					{editMode ? (
						<DatePicker
							format="MM-DD-YYYY"
							value={startDate}
							onChange={(value) => setStartDate(value)}
						/>
					) : (
						formatDate(bonus.startDate, languageCode, dateFormat)
					)}
				</Descriptions.Item>

				<Descriptions.Item
					label={ml('Eligible Date', currentUser, allMultiLingualData)}
				>
					{editMode ? (
						<DatePicker
							format="MM-DD-YYYY"
							value={earnedDate}
							onChange={(value) => setEarnedDate(value)}
						/>
					) : (
						formatDate(bonus.earnedDate, languageCode, dateFormat)
					)}
				</Descriptions.Item>

				{(paidDate || editMode) && (
					<Descriptions.Item
						label={ml('Paid Date', currentUser, allMultiLingualData)}
					>
						{editMode ? (
							<DatePicker
								format="MM-DD-YYYY"
								value={paidDate}
								onChange={(value) => setPaidDate(value)}
							/>
						) : (
							formatDate(bonus?.paidDate, languageCode, dateFormat)
						)}
					</Descriptions.Item>
				)}

				<Descriptions.Item
					label={ml('Status', currentUser, allMultiLingualData)}
				>
					{editMode ? (
						<Dropdown
							overlay={
								<div
									style={{
										backgroundColor: 'white',
										border: '1px solid #d9d9d9',
										borderRadius: '6px',
										boxShadow:
											'0 3px 6px -4px rgba(0,0,0,.12), 0 6px 16px 0 rgba(0,0,0,.08), 0 9px 28px 8px rgba(0,0,0,.05)',
									}}
								>
									{Object.keys(BONUS_STATUS).map((key) => (
										<div
											key={key}
											style={{
												padding: '8px 12px',
												cursor: 'pointer',
												backgroundColor: 'white',
												borderBottom: '1px solid #f0f0f0',
												color: getBonusTagColor(key),
											}}
											onClick={() => setBonusStatus(key)}
											onMouseEnter={(e) =>
												(e.target.style.backgroundColor = '#f5f5f5')
											}
											onMouseLeave={(e) =>
												(e.target.style.backgroundColor = 'white')
											}
										>
											{ml(
												mapBonusStatus(key, currentUser?.company),
												currentUser,
												allMultiLingualData
											)}
										</div>
									))}
								</div>
							}
							disabled={isRevalidating}
						>
							<Button>
								{ml(
									mapBonusStatus(bonusStatus, currentUser?.company),
									currentUser,
									allMultiLingualData
								)}
								<DownOutlined />
							</Button>
						</Dropdown>
					) : (
						<Space style={{ color: getBonusTagColor(bonus?.bonusStatus) }}>
							{bonus?.bonusStatus === 'earned'
								? ml(
										mapBonusStatus('earned', currentUser?.company),
										currentUser,
										allMultiLingualData
									)
								: bonus?.bonusStatus
									? ml(
											mapBonusStatus(
												bonus.bonusStatus,
												currentUser?.company,
												bonus.bonusStatus.charAt(0).toUpperCase() +
													bonus.bonusStatus.slice(1)
											),
											currentUser,
											allMultiLingualData
										)
									: null}
							{bonus.referralId && (
								<Tooltip
									title={
										isRevalidating
											? 'Revalidation in progress...'
											: 'Revalidate Bonus?'
									}
								>
									<Button
										type="default"
										size="small"
										onClick={() => {
											revalidateBonus(bonus);
											setIsRevalidating(true);
										}}
										shape="circle"
										disabled={isRevalidating}
										icon={<SyncOutlined spin={isRevalidating} />}
									/>
								</Tooltip>
							)}
						</Space>
					)}
				</Descriptions.Item>

				<Descriptions.Item label="Ignore Eligibility Checks">
					{editMode ? (
						<Space>
							<Switch
								checked={ignoreEligibilityChecks}
								onChange={(checked) => setIgnoreEligibilityChecks(checked)}
								disabled={isRevalidating}
							/>
							<Text>
								Ignore any further automated eligibility checks for this bonus
							</Text>
						</Space>
					) : (
						<Text>{ignoreEligibilityChecks ? 'Yes' : 'No'}</Text>
					)}
				</Descriptions.Item>
			</Descriptions>

			{/* Notes Section */}
			<Card size="small" style={{ marginTop: 16 }}>
				<Title level={5}>
					{ml('Bonus Notes', currentUser, allMultiLingualData)}
				</Title>
				{editMode ? (
					<TextArea
						value={notes}
						rows={4}
						onChange={(e) => setNotes(e.target.value)}
						placeholder={ml(
							'Add bonus notes...',
							currentUser,
							allMultiLingualData
						)}
					/>
				) : (
					<div>
						{bonus.notes ? (
							<Text>{bonus.notes}</Text>
						) : (
							<Text type="secondary">No notes added</Text>
						)}
						{canEdit && !editMode && (
							<div style={{ marginTop: 8 }}>
								<Button
									type="link"
									icon={<PlusCircleOutlined />}
									onClick={() => {
										setEditMode(true);
									}}
								>
									{ml('Add Note', currentUser, allMultiLingualData)}
								</Button>
							</div>
						)}
					</div>
				)}
			</Card>

			{/* Reason for Change - Only shown in edit mode for referrals */}
			{editMode && bonus.referralId && (
				<Card size="small" style={{ marginTop: 16 }}>
					<Title level={5}>
						<Text type="danger">* </Text>
						{ml('Reason for Change', currentUser, allMultiLingualData)}
					</Title>
					<Input
						value={reasonForChange}
						placeholder={`${ml('Provide a reason for change', currentUser, allMultiLingualData)} (${ml('required', currentUser, allMultiLingualData)})`}
						onChange={handleReasonForChange}
						style={{
							borderColor: validationError ? '#ff4d4f' : undefined,
						}}
					/>
					{validationError && (
						<div
							style={{
								color: '#ff4d4f',
								fontSize: '14px',
								marginTop: '4px',
							}}
						>
							{validationError}
						</div>
					)}
				</Card>
			)}

			{/* Logs Section */}
			{!editMode && renderLogs()}
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	fetchingEligibility: state.referrals.fetchingEligibility,
	revalidatingBonuses: state.referrals.revalidatingBonuses || {},
});

const mapDispatchToProps = {
	updateBonusStatus,
	revalidateBonus,
};

export default connect(mapStateToProps, mapDispatchToProps)(BonusDetailsModal);
